<template>
  <div class="guide-icon-wrapper">
    <div
      class="customize-icon"
      @click.stop="toggleGuideMenu"
    >
      <img
        :src="showGuideMenu ? require('@/assets/icons/work_click.png') : require('@/assets/icons/work.png')"
        :alt="$t('platformGuide.altIcon')"
        class="icon-img"
      >
    </div>
    <!-- 平台功能指南弹窗 -->
    <div
      v-if="showGuideMenu"
      v-loading="loading"
      class="guide-menu-panel"
      :element-loading-text="$t('platformGuide.loading')"
      @click.stop
    >
      <div class="guide-menu-title">
        {{ $t('platformGuide.title') }}
      </div>
      <div class="guide-menu-content">
        <div
          v-for="col in guideColumns"
          :key="col.type"
          class="guide-menu-column"
        >
          <div class="guide-menu-category">
            <span class="category-bar">|</span>
            <span class="category-title">{{ $t(col.titleKey) }}</span>
          </div>
          <div
            v-if="!col.items.length"
            class="guide-menu-empty"
          >
            {{ $t('platformGuide.empty') }}
          </div>
          <div
            v-for="item in col.items"
            v-else
            :key="item.id"
            class="guide-menu-item"
            :class="{ active: activeGuideItem === itemRowKey(item) }"
            @click="handleGuideItemClick(item)"
          >
            {{ itemDisplayName(item, $t('platformGuide.unnamed')) }}
          </div>
        </div>
      </div>
    </div>
    <!-- 遮罩层，点击关闭菜单 -->
    <div
      v-if="showGuideMenu"
      class="guide-menu-overlay"
      @click="closeGuideMenu"
    />
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { listFunctionGuide } from '@/api/functionGuide'
import {
  buildGuideColumns,
  itemDisplayName,
  itemRowKey
} from '@/utils/functionGuideUtils'

export default {
  name: 'PlatformGuide',
  data() {
    return {
      showGuideMenu: false,
      activeGuideItem: '',
      loading: false,
      functionGuideList: []
    }
  },
  computed: {
    guideColumns() {
      return buildGuideColumns(this.functionGuideList)
    }
  },
  created() {
    this.loadFunctionGuideList()
  },
  methods: {
    itemRowKey,
    itemDisplayName,
    async loadFunctionGuideList() {
      this.loading = true
      try {
        const res = await listFunctionGuide({
          pageNum: 1,
          pageSize: 500
        })
        const rows = res?.rows ?? res?.data?.rows ?? []
        this.functionGuideList = Array.isArray(rows) ? rows : []
      } catch (e) {
        console.error('listFunctionGuide failed', e)
        this.functionGuideList = []
        ElMessage.error(this.$t('platformGuide.loadFailed'))
      } finally {
        this.loading = false
      }
    },
    toggleGuideMenu() {
      this.showGuideMenu = !this.showGuideMenu
      if (this.showGuideMenu) {
        this.$emit('menu-toggle', 'guide')
        if (!this.functionGuideList.length && !this.loading) {
          this.loadFunctionGuideList()
        }
      }
    },
    closeGuideMenu() {
      this.showGuideMenu = false
    },
    handleGuideItemClick(item) {
      const id = item?.id
      if (id == null || id === '') {
        ElMessage.warning(this.$t('platformGuide.missingId'))
        return
      }
      this.activeGuideItem = String(id)
      const routeData = this.$router.resolve({
        name: 'ArticleDetail',
        query: { guideId: String(id) }
      })
      window.open(routeData.href, '_blank', 'noopener,noreferrer')
      this.closeGuideMenu()
    }
  }
}
</script>

<style lang="scss" scoped>
.guide-icon-wrapper {
  position: relative;

  .customize-icon {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 4px 8px;
    transition: opacity 0.3s;

    .icon-img {
      width: 23px;
      height: 23px;
      display: block;
      transition: opacity 0.3s;
    }

    &:hover .icon-img {
      opacity: 0.7;
    }
  }

  // 平台功能指南弹窗
  .guide-menu-panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 600px;
    z-index: 1001;
    overflow: hidden;

    // 三角形指示器
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      right: 20px;
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 6px solid #ffffff;
    }

    .guide-menu-title {
      padding: 16px 20px;
      font-size: 16px;
      font-weight: bold;
      color: #000000;
      border-bottom: 1px solid #f0f0f0;
    }

    .guide-menu-content {
      display: flex;
      padding: 16px 20px;
      gap: 32px;

      .guide-menu-column {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;

        .guide-menu-category {
          display: flex;
          align-items: center;
          margin-bottom: 4px;

          .category-bar {
            color: #0d63f3;
            font-weight: bold;
            margin-right: 8px;
            font-size: 16px;
          }

          .category-title {
            font-size: 14px;
            font-weight: 600;
            color: #000000;
          }
        }

        .guide-menu-empty {
          padding: 8px 12px;
          font-size: 13px;
          color: #999;
        }

        .guide-menu-item {
          padding: 8px 12px;
          font-size: 14px;
          color: #000000;
          cursor: pointer;
          transition: all 0.2s;
          border-radius: 4px;

          &:hover {
            background-color: #f5f7fa;
          }

          &.active {
            background-color: #f4f7ff;
            color: #175be5;
          }
        }
      }
    }
  }

  // 遮罩层
  .guide-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background: transparent;
  }
}

// 移动端适配
@media (max-width: 768px) {
  .guide-icon-wrapper {
    .customize-icon {
      padding: 4px 6px;

      .icon-img {
        width: 20px;
        height: 20px;
      }
    }

    .guide-menu-panel {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      min-width: 100%;
      max-height: 70vh;
      overflow-y: auto;
      border-radius: 16px 16px 0 0;

      &::before {
        display: none; // 移动端隐藏三角形
      }

      .guide-menu-title {
        padding: 12px 16px;
        font-size: 16px;
        position: sticky;
        top: 0;
        background: #ffffff;
        z-index: 1;
        border-bottom: 1px solid #f0f0f0;
      }

      .guide-menu-content {
        padding: 12px 16px;
        flex-direction: column;
        gap: 0;

        .guide-menu-column {
          margin-bottom: 20px;

          &:last-child {
            margin-bottom: 0;
          }

          .guide-menu-category {
            margin-bottom: 8px;

            .category-title {
              font-size: 14px;
            }
          }

          .guide-menu-item {
            padding: 10px 12px;
            font-size: 14px;
          }
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .guide-icon-wrapper {
    .customize-icon {
      padding: 4px;

      .icon-img {
        width: 18px;
        height: 18px;
      }
    }
  }
}
</style>
